import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import {
  useDriverAllowanceServiceV1BatchCreateDriverAllowanceMutation,
  useDriverAllowanceServiceV1GetDriverAllowanceVehicleTypesQuery,
  useDriverAllowanceServiceV1GetDriverAllowanceVehicleModelsQuery,
  useDriverAllowanceServiceV1GetDriverAllowanceTripTypesQuery,
  useDriverAllowanceServiceV1GetDriverAllowanceDistanceTypesQuery,
  useDriverAllowanceServiceV1GetDriverAllowanceWaitingTimeRatesQuery,
} from "_services/modules/driver_allowance";
import { useVehicleAuxiliaryServiceV1ListVehicleAuxiliariesQuery } from "_services/modules/vehicle-auxiliary";
import { parseFormattedNumber } from "_helper/formatters";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";
interface DriverAllowanceItem {
  vehicleTypeId: string;
  vehicleTypeName: string;
  vehicleModelId: string;
  vehicleModelName: string;
  vehicleAttachmentIds: string[];
  vehicleAttachmentNames: string[];
  distanceTypeId: string;
  distanceTypeName: string;
  distanceTypeRange: string;
  tripTypeId: string;
  tripTypeName: string;
  driverBasicRate: string;
  driverIncentiveRate: string;
  waitingTimeRateId: string;
  waitingTimeRateName: string;
  status: string;
}

export interface AddDriverAllowanceFormValues {
  vehicleTypeId: string;
  vehicleModelId: string;
  vehicleAttachmentIds: string[];
  distanceTypeId: string;
  tripTypeId: string;
  driverBasicRate: string;
  driverIncentiveRate: string;
  waitingTimeRateId: string;
  status: string;
  items: DriverAllowanceItem[];
}

const schema = yup.object({
  vehicleTypeId: yup.string().required("Vehicle Type is required"),
  vehicleModelId: yup.string().required("Vehicle Model is required"),
  vehicleAttachmentIds: yup.array().of(yup.string().required()).default([]),
  distanceTypeId: yup.string().required("Distance Type is required"),
  tripTypeId: yup.string().required("Trip Type is required"),
  driverBasicRate: yup.string().required("Driver Basic Rate is required").test(
    "is-positive-number",
    "Must be a positive number",
    (value) => {
      if (!value) return false;
      const num = parseFormattedNumber(value);
      return !isNaN(num) && num > 0;
    }
  ),
  driverIncentiveRate: yup.string().required("Driver Incentive Rate is required").test(
    "is-non-negative-number",
    "Must be a non-negative number",
    (value) => {
      if (!value && value !== "0") return false;
      const num = parseFormattedNumber(value);
      return !isNaN(num) && num >= 0;
    }
  ),
  waitingTimeRateId: yup.string().required("Waiting Time Rate is required"),
  status: yup.string().required("Status is required"),
  items: yup.array().of(
    yup.object({
      vehicleTypeId: yup.string().required(),
      vehicleTypeName: yup.string().required(),
      vehicleModelId: yup.string().required(),
      vehicleModelName: yup.string().required(),
      vehicleAttachmentIds: yup.array().of(yup.string().required()).required(),
      vehicleAttachmentNames: yup.array().of(yup.string().required()).required(),
      distanceTypeId: yup.string().required(),
      distanceTypeName: yup.string().required(),
      distanceTypeRange: yup.string().required(),
      tripTypeId: yup.string().required(),
      tripTypeName: yup.string().required(),
      driverBasicRate: yup.string().required(),
      driverIncentiveRate: yup.string().required(),
      waitingTimeRateId: yup.string().required(),
      waitingTimeRateName: yup.string().required(),
      status: yup.string().required(),
    })
  ).required(),
}).required();

const useAddDriverAllowanceForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("driverAllowance");

  const [batchCreateDriverAllowance] = useDriverAllowanceServiceV1BatchCreateDriverAllowanceMutation();

  const { data: vehicleTypesData } = useDriverAllowanceServiceV1GetDriverAllowanceVehicleTypesQuery();
  const { data: distanceTypesData } = useDriverAllowanceServiceV1GetDriverAllowanceDistanceTypesQuery();
  const { data: waitingTimeRatesData } = useDriverAllowanceServiceV1GetDriverAllowanceWaitingTimeRatesQuery();
  const { data: vehicleAuxiliaryData } = useVehicleAuxiliaryServiceV1ListVehicleAuxiliariesQuery({ status: "active" });

  const vehicleTypes = vehicleTypesData?.vehicle_types ?? [];
  const distanceTypes = distanceTypesData?.distance_types ?? [];
  const waitingTimeRates = waitingTimeRatesData?.waiting_time_rates ?? [];
  const attachmentOptions = vehicleAuxiliaryData?.vehicle_auxiliary?.map((a) => ({
    id: a.id ?? "",
    name: a.auxiliary_name ?? "",
  })) ?? [];

  const {
    control,
    register,
    trigger,
    getValues,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<AddDriverAllowanceFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      vehicleTypeId: "",
      vehicleModelId: "",
      vehicleAttachmentIds: [],
      distanceTypeId: "",
      tripTypeId: "",
      driverBasicRate: "",
      driverIncentiveRate: "",
      waitingTimeRateId: "",
      status: "active",
      items: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "items",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>({});

  const vehicleTypeId = watch("vehicleTypeId");
  const selectedVehicleType = vehicleTypes.find((vt) => vt.id === vehicleTypeId);
  const showAttachment = !!(selectedVehicleType as any)?.is_add_on;

  const { data: vehicleModelsData } = useDriverAllowanceServiceV1GetDriverAllowanceVehicleModelsQuery(
    { vehicleTypeId },
    { skip: !vehicleTypeId }
  );
  const { data: tripTypesData } = useDriverAllowanceServiceV1GetDriverAllowanceTripTypesQuery();

  const vehicleModelOptions = vehicleModelsData?.vehicle_models ?? [];
  const tripTypeOptions = tripTypesData?.trip_types ?? [];

  useEffect(() => {
    setValue("vehicleModelId", "");
    setValue("vehicleAttachmentIds", []);
    setValue("tripTypeId", "");
  }, [vehicleTypeId, setValue]);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-add"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const toggleExpand = (index: number) => {
    setExpandedItems((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const getDisplayNames = () => {
    const values = getValues();
    const vehicleType = vehicleTypes.find((vt) => vt.id === values.vehicleTypeId);
    const vehicleModel = vehicleModelOptions.find((vm) => vm.id === values.vehicleModelId);
    const distanceType = distanceTypes.find((dt) => dt.id === values.distanceTypeId);
    const tripType = tripTypeOptions.find((tt) => tt.id === values.tripTypeId);
    const waitingTimeRate = waitingTimeRates.find((wtr) => wtr.id === values.waitingTimeRateId);

    return {
      vehicleTypeName: vehicleType?.name ?? "",
      vehicleModelName: vehicleModel?.name ?? "",
      distanceTypeName: distanceType?.name ?? "",
      distanceTypeRange: distanceType?.name ?? "",
      tripTypeName: tripType?.name ?? "",
      waitingTimeRateName: waitingTimeRate?.name ?? "",
      vehicleAttachmentNames: attachmentOptions
        .filter((a) => values.vehicleAttachmentIds.includes(a.id))
        .map((a) => a.name),
    };
  };

  const handleAddToList = async () => {
    const fieldsToValidate: (keyof AddDriverAllowanceFormValues)[] = [
      "vehicleTypeId", "vehicleModelId", "distanceTypeId", "tripTypeId",
      "driverBasicRate", "driverIncentiveRate", "waitingTimeRateId", "status",
    ];
    const isValid = await trigger(fieldsToValidate);
    if (!isValid) return;

    const values = getValues();
    const names = getDisplayNames();

    // Duplicate check: (vehicleTypeId, vehicleModelId, distanceTypeId, tripTypeId)
    const isDuplicate = fields.some(
      (item, index) =>
        item.vehicleTypeId === values.vehicleTypeId &&
        item.vehicleModelId === values.vehicleModelId &&
        item.distanceTypeId === values.distanceTypeId &&
        item.tripTypeId === values.tripTypeId &&
        index !== editingIndex
    );

    if (isDuplicate) {
      setError("tripTypeId", {
        type: "validate",
        message: t("error-duplicate-value"),
      });
      return;
    }

    const newItem: DriverAllowanceItem = {
      vehicleTypeId: values.vehicleTypeId,
      vehicleTypeName: names.vehicleTypeName,
      vehicleModelId: values.vehicleModelId,
      vehicleModelName: names.vehicleModelName,
      vehicleAttachmentIds: values.vehicleAttachmentIds,
      vehicleAttachmentNames: names.vehicleAttachmentNames,
      distanceTypeId: values.distanceTypeId,
      distanceTypeName: names.distanceTypeName,
      distanceTypeRange: names.distanceTypeRange,
      tripTypeId: values.tripTypeId,
      tripTypeName: names.tripTypeName,
      driverBasicRate: values.driverBasicRate,
      driverIncentiveRate: values.driverIncentiveRate,
      waitingTimeRateId: values.waitingTimeRateId,
      waitingTimeRateName: names.waitingTimeRateName,
      status: values.status,
    };

    if (editingIndex !== null) {
      update(editingIndex, newItem);
      setEditingIndex(null);
    } else {
      append(newItem);
    }

    resetForm();
  };

  const resetForm = () => {
    setValue("vehicleTypeId", "");
    setValue("vehicleModelId", "");
    setValue("vehicleAttachmentIds", []);
    setValue("distanceTypeId", "");
    setValue("tripTypeId", "");
    setValue("driverBasicRate", "");
    setValue("driverIncentiveRate", "");
    setValue("waitingTimeRateId", "");
    setValue("status", "active");
  };

  const handleEditItem = (index: number) => {
    const item = fields[index];
    setEditingIndex(index);
    setValue("vehicleTypeId", item.vehicleTypeId);
    setValue("vehicleModelId", item.vehicleModelId);
    setValue("vehicleAttachmentIds", item.vehicleAttachmentIds);
    setValue("distanceTypeId", item.distanceTypeId);
    setValue("tripTypeId", item.tripTypeId);
    setValue("driverBasicRate", item.driverBasicRate);
    setValue("driverIncentiveRate", item.driverIncentiveRate);
    setValue("waitingTimeRateId", item.waitingTimeRateId);
    setValue("status", item.status);
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
    if (editingIndex === index) {
      setEditingIndex(null);
      resetForm();
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleDiscardChanges = () => {
    setEditingIndex(null);
    resetForm();
  };

  const handleSave = async () => {
    try {
      const payload = fields.map((f) => ({
        vehicle_type_id: f.vehicleTypeId,
        vehicle_model_id: f.vehicleModelId,
        vehicle_attachment_ids: f.vehicleAttachmentIds,
        distance_type_id: f.distanceTypeId,
        trip_type_id: f.tripTypeId,
        driver_basic_rate: parseFormattedNumber(f.driverBasicRate),
        driver_insentif_rate: parseFormattedNumber(f.driverIncentiveRate),
        waiting_time_rate_id: f.waitingTimeRateId,
        status: f.status,
      }));
      await batchCreateDriverAllowance({
        driverAllowanceV1BatchCreateDriverAllowanceReq: {
          driver_allowances: payload,
        },
      }).unwrap();
      toast.success(t("toast-create-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error) {
      errorHandler(error);
      setShowSaveModal(false);
    }
  };

  const handleCancel = () => {
    if (fields.length > 0) {
      setShowCancelModal(true);
    } else {
      navigate(-1);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  const formatRupiah = (value: string) => {
    const num = parseFormattedNumber(value);
    if (isNaN(num)) return value;
    return `Rp. ${num.toLocaleString("id-ID")}`;
  };

  return {
    control,
    register,
    errors,
    fields,
    editingIndex,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    showAttachment,
    vehicleModelOptions,
    tripTypeOptions,
    expandedItems,
    toggleExpand,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    handleConfirmCancel,
    formatRupiah,
    t,
    // Dropdown data
    vehicleTypes,
    distanceTypes,
    waitingTimeRates,
    attachmentOptions,
  };
};

export default useAddDriverAllowanceForm;
